import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import analyticsService from './services/AnalyticsService.ts';
import { useCart } from './contexts/CartContext.tsx';
import './Header.css';
import logoGreen from './sweetspot-logo-green.png';

interface HeaderProps {
  onMenuToggle?: (isOpen: boolean) => void;
}

// Individual store items (kept for URL matching and analytics)
const storeItems = [
  { id: 1, storeId: '5796', name: 'West Hartford, CT - MED', state: 'connecticut', city: 'west-hartford', menuType: 'medical' },
  { id: 2, storeId: '5797', name: 'West Hartford, CT - REC', state: 'connecticut', city: 'west-hartford', menuType: 'recreational' },
  { id: 3, storeId: '857', name: 'Olney, MD - MED', state: 'maryland', city: 'olney', menuType: 'medical' },
  { id: 4, storeId: '5440', name: 'Olney, MD - REC', state: 'maryland', city: 'olney', menuType: 'recreational' },
  { id: 5, storeId: '4855', name: 'Voorhees, NJ - MED', state: 'new-jersey', city: 'voorhees', menuType: 'medical' },
  { id: 6, storeId: '5377', name: 'Voorhees, NJ - REC', state: 'new-jersey', city: 'voorhees', menuType: 'recreational' },
  { id: 10, storeId: '696002abeb54a2091b709e0f', name: 'Hamilton, NJ - REC', state: 'new-jersey', city: 'hamilton', menuType: 'recreational' },
  { id: 14, storeId: '6248', name: 'Mount Olive, NJ - REC', state: 'new-jersey', city: 'mount-olive', menuType: 'recreational' },
  { id: 8, storeId: '6247', name: 'River Edge, NJ - REC', state: 'new-jersey', city: 'river-edge', menuType: 'recreational' },
  { id: 9, storeId: '4545', name: 'Exeter, RI - MED', state: 'rhode-island', city: 'exeter', menuType: 'medical' },
  { id: 10, storeId: '4996', name: 'Exeter, RI - REC', state: 'rhode-island', city: 'exeter', menuType: 'recreational' },
  { id: 11, storeId: '6145', name: 'Essex Junction, VT - REC', state: 'vermont', city: 'essex-junction', menuType: 'recreational' },
  { id: 12, storeId: '6158', name: 'Stamford, CT - MED', state: 'connecticut', city: 'stamford', menuType: 'medical' },
  { id: 13, storeId: '6159', name: 'Stamford, CT - REC', state: 'connecticut', city: 'stamford', menuType: 'recreational' }
];

// Stores grouped by state for the new dropdown UI
const storesByState: Record<string, typeof storeItems> = {
  'CONNECTICUT': [
    { id: 12, storeId: '6158', name: 'Stamford, CT - MED', state: 'connecticut', city: 'stamford', menuType: 'medical' },
    { id: 13, storeId: '6159', name: 'Stamford, CT - REC', state: 'connecticut', city: 'stamford', menuType: 'recreational' },
    { id: 1, storeId: '5796', name: 'West Hartford, CT - MED', state: 'connecticut', city: 'west-hartford', menuType: 'medical' },
    { id: 2, storeId: '5797', name: 'West Hartford, CT - REC', state: 'connecticut', city: 'west-hartford', menuType: 'recreational' },
  ],
  'MARYLAND': [
    { id: 3, storeId: '857', name: 'Olney, MD - MED', state: 'maryland', city: 'olney', menuType: 'medical' },
    { id: 4, storeId: '5440', name: 'Olney, MD - REC', state: 'maryland', city: 'olney', menuType: 'recreational' },
  ],
  'NEW JERSEY': [
    { id: 10, storeId: '696002abeb54a2091b709e0f', name: 'Hamilton, NJ - REC', state: 'new-jersey', city: 'hamilton', menuType: 'recreational' },
    { id: 14, storeId: '6248', name: 'Mount Olive, NJ - REC', state: 'new-jersey', city: 'mount-olive', menuType: 'recreational' },
    { id: 8, storeId: '6247', name: 'River Edge, NJ - REC', state: 'new-jersey', city: 'river-edge', menuType: 'recreational' },
    { id: 5, storeId: '4855', name: 'Voorhees, NJ - MED', state: 'new-jersey', city: 'voorhees', menuType: 'medical' },
    { id: 6, storeId: '5377', name: 'Voorhees, NJ - REC', state: 'new-jersey', city: 'voorhees', menuType: 'recreational' },
  ],
  'RHODE ISLAND': [
    { id: 9, storeId: '4545', name: 'Exeter, RI - MED', state: 'rhode-island', city: 'exeter', menuType: 'medical' },
    { id: 10, storeId: '4996', name: 'Exeter, RI - REC', state: 'rhode-island', city: 'exeter', menuType: 'recreational' },
  ],
  'VERMONT': [
    { id: 11, storeId: '6145', name: 'Essex Junction, VT - REC', state: 'vermont', city: 'essex-junction', menuType: 'recreational' },
  ],
};

// Order of states in the dropdown
const stateOrder = ['CONNECTICUT', 'MARYLAND', 'NEW JERSEY', 'RHODE ISLAND', 'VERMONT'];

const Header: React.FC<HeaderProps> = ({ onMenuToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentRetailerId, setRetailerId, clearCart } = useCart();
  const isHome = location.pathname === '/';
  const isSustainability = location.pathname === '/sustainability'; // Add this line
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(!isHome); // Initialize based on route
  const [isLocationMenuOpen, setIsLocationMenuOpen] = useState(false);
  const [isLocationMenuSticky, setIsLocationMenuSticky] = useState(false);
  const [, setSelectedStore] = useState<string | null>(null);
  const [selectedStoreName, setSelectedStoreName] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1117);
  const [expandedState, setExpandedState] = useState<string | null>(null);
  const [isNarrow, setIsNarrow] = useState(window.innerWidth <= 840);
  const [isUltraNarrow, setIsUltraNarrow] = useState(window.innerWidth <= 760);

  // Auto-select store based on current menu URL
  useEffect(() => {
    const pathname = location.pathname;
    
    // Check if we're on a menu page (old or new URL structure)
    if (pathname.startsWith('/menu/')) {
      // Old URL structure: /menu/{menuId}
      const menuId = pathname.split('/')[2];
      const matchingStore = storeItems.find(store => store.storeId === menuId);
      if (matchingStore) {
        setSelectedStore(matchingStore.storeId);
        setSelectedStoreName(matchingStore.name);
      }
    } else if (pathname.includes('/dispensary-')) {
      // New URL structure: /store/{state}/{city}/dispensary-{type}-menu
      const pathParts = pathname.split('/');
      if (pathParts.length >= 4 && pathParts[1] === 'store') {
        const state = pathParts[2];
        const city = pathParts[3];
        const isMedical = pathname.includes('dispensary-medical-menu');
        const menuType = isMedical ? 'medical' : 'recreational';
        
        const matchingStore = storeItems.find(store => 
          store.state === state && 
          store.city === city && 
          store.menuType === menuType
        );
        
        if (matchingStore) {
          setSelectedStore(matchingStore.storeId);
          setSelectedStoreName(matchingStore.name);
        }
      }
    } else {
      // Not on a menu page, clear selection
      setSelectedStore(null);
      setSelectedStoreName(null);
    }
  }, [location.pathname]);

  useEffect(() => {
    // If not on home page, always set scrolled to true
    if (!isHome) {
      setIsScrolled(true);
      return;
    }

    // Only add scroll listener on home page
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHome]); // Add isHome as dependency

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1117);
      setIsNarrow(window.innerWidth <= 840);
      setIsUltraNarrow(window.innerWidth <= 760);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Click away listener for sticky dropdown
  useEffect(() => {
    const handleClickAway = (event: MouseEvent) => {
      if (!isLocationMenuSticky) return;
      
      const target = event.target as Element;
      if (!target.closest('.location-menu-container')) {
        setIsLocationMenuOpen(false);
        setIsLocationMenuSticky(false);
      }
    };

    if (isLocationMenuSticky) {
      document.addEventListener('click', handleClickAway);
    }

    return () => {
      document.removeEventListener('click', handleClickAway);
    };
  }, [isLocationMenuSticky]);

  const formatStoreName = (storeName: string) => {
    if (isUltraNarrow) {
      // At 760px and below, show only MED or REC
      const match = storeName.match(/(MED|REC)$/);
      return match ? match[1] : storeName;
    }
    
    if (isNarrow) {
      // At 840px and below, remove state and dash
      // "Olney, MD - REC" -> "OLNEY REC"
      return storeName
        .replace(/,\s*[A-Z]{2}\s*-\s*/, ' ') // Remove ", XX - "
        .toUpperCase();
    }
    
    // Above 840px, show full name
    return storeName;
  };

  const toggleMenu = () => {
    const newState = !isMobileMenuOpen;
    setIsMobileMenuOpen(newState);
    onMenuToggle?.(newState);
    
    // Track mobile menu interaction
    analyticsService.trackEvent('menu_interaction', {
      menu_type: 'mobile_hamburger',
      action: newState ? 'open' : 'close',
      page_path: location.pathname
    });
  };

  const handleMouseEnter = () => {
    // Only allow hover if no store selected AND on root URL
    if (!selectedStoreName && isHome) {
      setIsLocationMenuOpen(true);
      // Don't make sticky on hover - only on click
    }
  };

  const handleMouseLeave = () => {
    // Only respond to mouse leave if hover is enabled and not sticky
    if (!isLocationMenuSticky && !selectedStoreName && isHome) {
      setIsLocationMenuOpen(false);
      setExpandedState(null); // Reset expanded state when closing
    }
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isLocationMenuOpen && isLocationMenuSticky) {
      // If it's open and sticky, close it
      setIsLocationMenuOpen(false);
      setIsLocationMenuSticky(false);
      setExpandedState(null); // Reset expanded state when closing
    } else {
      // Open it and make it sticky
      setIsLocationMenuOpen(true);
      setIsLocationMenuSticky(true);
    }
  };

  const handleCloseMenu = () => {
    setIsLocationMenuOpen(false);
    setIsLocationMenuSticky(false);
    setExpandedState(null);
  };

  // Handle state click in dropdown (accordion behavior)
  const handleStateClick = (stateName: string) => {
    if (expandedState === stateName) {
      setExpandedState(null); // Collapse if already expanded
    } else {
      setExpandedState(stateName); // Expand this state, collapse others
    }
  };

  // Format store name for dropdown display: "City (TYPE)"
  const formatDropdownStoreName = (store: typeof storeItems[0]) => {
    // Extract city from the city slug (e.g., "west-hartford" -> "West Hartford")
    const cityFormatted = store.city
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    const typeLabel = store.menuType === 'medical' ? 'MED' : 'REC';
    return `${cityFormatted} [${typeLabel}]`;
  };

  const handleStoreClick = (storeId: string) => {
    // Clear cart when switching stores (different storeId means different store)
    if (currentRetailerId && currentRetailerId !== storeId) {
      clearCart();
    }
    
    // Find the store details for analytics and navigation
    const selectedStoreData = storeItems.find(store => store.storeId === storeId);
    if (selectedStoreData) {
      // Extract state from store name (e.g., "West Hartford, CT - MED" -> "CT")
      const stateMatch = selectedStoreData.name.match(/,\s*([A-Z]{2})/);
      const state = stateMatch ? stateMatch[1] : '';
      
      analyticsService.trackStoreSelected({
        store_id: storeId,
        store_name: selectedStoreData.name,
        store_state: state,
        selection_method: 'dropdown'
      });
      
      // Set the selected store name for display
      setSelectedStoreName(selectedStoreData.name);
      
      // Update the retailer ID for cart context
      setRetailerId(storeId);
      
      // Navigate to the new URL structure
      const menuPath = `/store/${selectedStoreData.state}/${selectedStoreData.city}/dispensary-${selectedStoreData.menuType}-menu`;
      navigate(menuPath);
    }

    setSelectedStore(storeId);
    setIsLocationMenuOpen(false); // Close the dropdown after selection
    setIsLocationMenuSticky(false); // Remove sticky state
    setExpandedState(null); // Reset expanded state
    setIsMobileMenuOpen(false); // Close the hamburger menu
    onMenuToggle?.(false); // Notify parent that menu closed
  };

  return (
    <header className={`App-header ${isHome ? 'home' : ''} ${isSustainability ? 'sustainability-page' : ''} ${isScrolled ? 'scrolled' : ''}`}>
      <div 
        className={`hamburger-button ${isMobileMenuOpen ? 'open' : ''}`}
        onClick={toggleMenu}
      >
        <span></span>
        <span></span>
        <span></span>
      </div>
      
      <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
        <a href="/store" onClick={() => analyticsService.trackNavigation({
          link_text: 'STORES',
          destination: '/store',
          source_page: location.pathname
        })}>STORES</a>
        <a href="/sustainability" onClick={() => analyticsService.trackNavigation({
          link_text: 'SUSTAINABILITY',
          destination: '/sustainability',
          source_page: location.pathname
        })}>SUSTAINABILITY</a>
        <a href="/franchising"
           className="franchise-link"
           onClick={() => analyticsService.trackNavigation({
             link_text: 'FRANCHISE',
             destination: '/franchising',
             source_page: location.pathname
           })}>FRANCHISE</a>
      </div>

      <nav className="nav-links">
        <a href="/store" onClick={() => analyticsService.trackNavigation({
          link_text: 'STORES',
          destination: '/store',
          source_page: location.pathname
        })}>STORES</a>
        <a href="/sustainability" onClick={() => analyticsService.trackNavigation({
          link_text: 'SUSTAINABILITY',
          destination: '/sustainability',
          source_page: location.pathname
        })}>SUSTAINABILITY</a>
        <a href="/franchising"
           className="franchise-link"
           onClick={() => analyticsService.trackNavigation({
             link_text: 'FRANCHISE',
             destination: '/franchising',
             source_page: location.pathname
           })}>FRANCHISE</a>
      </nav>
      <div className="logo-container">
        <a href="/" onClick={() => analyticsService.trackNavigation({
          link_text: 'LOGO',
          destination: '/',
          source_page: location.pathname
        })}>
          <img src={logoGreen} alt="Logo" className="logo-scaled" />
        </a>
      </div>
      <div className="location-menu-container"
           onMouseEnter={!selectedStoreName && isHome ? handleMouseEnter : undefined}
           onMouseLeave={!selectedStoreName && isHome ? handleMouseLeave : undefined}>
        <a href="#"
           className={`shop-by-location ${isLocationMenuOpen ? 'active' : ''}`}
           onClick={handleButtonClick}>
          {selectedStoreName ? formatStoreName(selectedStoreName) : (isMobile ? 'SHOP' : 'SHOP BY LOCATION')}
        </a>
        <div className={`location-dropdown ${isLocationMenuOpen ? 'open' : ''}`}>
          <div className={`location-dropdown-content ${isMobile ? 'mobile' : ''}`}>
            {isMobile ? (
              /* Mobile: Full-screen accordion with close button */
              <>
                <div className="state-list">
                  {stateOrder.map((stateName) => (
                    <React.Fragment key={stateName}>
                      <div
                        className={`state-item ${expandedState === stateName ? 'expanded' : ''}`}
                        onClick={() => handleStateClick(stateName)}
                      >
                        <span className="state-name">{stateName}</span>
                        <svg className="mobile-chevron" width="14" height="8" viewBox="0 0 14 8" fill="none">
                          <path d="M1 1L7 7L13 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <div className={`mobile-stores-accordion ${expandedState === stateName ? 'open' : ''}`}>
                        {storesByState[stateName]?.map((store) => (
                          <div
                            key={store.id}
                            className={`store-item ${store.isComingSoon ? 'coming-soon' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!store.isComingSoon) {
                                handleStoreClick(store.storeId);
                              }
                            }}
                          >
                            {store.isComingSoon
                              ? formatDropdownStoreName(store).replace('[REC]', '[Coming Soon]')
                              : formatDropdownStoreName(store)
                            }
                          </div>
                        ))}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
                <div className="mobile-close-button">
                  <button onClick={handleCloseMenu}>CLOSE</button>
                </div>
              </>
            ) : (
              /* Desktop: Two-column layout */
              <>
                <div className="state-list">
                  {stateOrder.map((stateName) => (
                    <div
                      key={stateName}
                      className={`state-item ${expandedState === stateName ? 'expanded' : ''}`}
                      onMouseEnter={() => setExpandedState(stateName)}
                    >
                      <span className="state-name">{stateName}</span>
                      <svg className="chevron" width="8" height="14" viewBox="0 0 8 14" fill="none" style={{ width: '8.5px', height: '14px' }}>
                        <path d="M1 1L7 7L1 13" stroke="white" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  ))}
                </div>
                {expandedState && (
                  <div className="store-list">
                    {storesByState[expandedState]?.map((store) => (
                      <div
                        key={store.id}
                        className={`store-item ${store.isComingSoon ? 'coming-soon' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!store.isComingSoon) {
                            handleStoreClick(store.storeId);
                          }
                        }}
                      >
                        {store.isComingSoon
                          ? formatDropdownStoreName(store).replace('[REC]', '[Coming Soon]')
                          : formatDropdownStoreName(store)
                        }
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <hr className="header-line" />
    </header>
  );
};

export default Header;